/*
Contains schemas for most types of Crystallize components. They should stay in the same file, as some of them are recursive and reference each other. By putting them into separate JS modules, we would have circular imports, which might break.
 */
import type { BaseIssue, BaseSchema, GenericPipeItem, GenericSchema, InferOutput } from 'valibot'
import type { NormalizedComponents } from '~/utils/crystallize/component-utils'
import { array, boolean, custom, date, fallback, intersect, lazy, literal, nullish, number, object, picklist, pipe, string, transform, union, unknown, url } from 'valibot'
import { transformComponents } from '~/utils/crystallize/component-utils'

function component<TType extends string, TContent extends GenericSchema>(
  type: TType,
  content: TContent,
) {
  return object({
    id: string(),
    type: literal(type),
    content: nullish(content),
  })
}

/**
 * Boolean/Switch
 *
 * @see https://crystallize.com/learn/developer-guides/catalogue-api/fetching-an-item/components#switch-component
 */
export const booleanData = boolean()

export type BooleanData = InferOutput<typeof booleanData>

export const booleanContent = object({ value: booleanData })

export const booleanComponent = component('boolean', booleanContent)

/**
 * Numeric
 *
 * @see https://crystallize.com/learn/developer-guides/catalogue-api/fetching-an-item/components#numeric-content
 */
export const numericData = number()

export type NumericData = InferOutput<typeof numericData>

export const numericContent = object({ number: numericData })

export const numericComponent = component('numeric', numericContent)

/**
 * SingleLine
 *
 * @see https://crystallize.com/learn/developer-guides/catalogue-api/fetching-an-item/components#single-line
 */
export const singleLineData = string()

export type SingleLineData = InferOutput<typeof singleLineData>

export const singleLineContent = object({ text: singleLineData })

export const singleLineComponent = component('singleLine', singleLineContent)

/**
 * Date
 *
 * @see https://crystallize.com/learn/developer-guides/catalogue-api/fetching-an-item/components#date
 */
export const datetimeData = pipe(
  // From Crystallize, this is always a string, but since this schema contains an internal transform to a Date, we support dates and numbers as inputs as well to make parsing idempotent.
  union([
    string(),
    date(),
    number(),
  ]),
  transform(it => new Date(it)),
)

export type DatetimeData = InferOutput<typeof datetimeData>

export const datetimeContent = object({
  datetime: datetimeData,
})

export const datetimeComponent = component('datetime', datetimeContent)

/**
 * Selection
 *
 * @see https://crystallize.com/learn/developer-guides/catalogue-api/fetching-an-item/components#selection-component
 */
export const selectionData = array(object({
  key: string(),
  value: string(),
}))

export type SelectionData = InferOutput<typeof selectionData>

export const selectionContent = object({
  options: selectionData,
})

export const selectionComponent = component('selection', selectionContent)

/**
 * RichText
 *
 * @see https://crystallize.com/learn/developer-guides/catalogue-api/fetching-an-item/components#rich-text
 */
const jsonMarkupType = union([
  object({
    metadata: nullish(unknown()),
    type: nullish(
      picklist([
        'paragraph',
        'strong',
        'line-break',
        'unordered-list',
        'ordered-list',
        'list',
        'list-item',
        'quote',
        'preformatted',
        'code',
        'underlined',
        'emphasized',
        'div',
        'span',
        'heading1',
        'heading2',
        'heading3',
        'heading4',
        'heading5',
        'heading6',
        'deleted',
        'inserted',
        'subscripted',
        'superscripted',
        'horizontal-line',
        'table',
        'table-row',
        'table-cell',
        'table-head-cell',
      ]),
    ),
  }),
  object({
    type: literal('link'),
    metadata: nullish(object({
      href: nullish(string()),
      target: nullish(string()),
      rel: nullish(string()),
    })),
  }),
])

type JsonMarkupTypeSchema = typeof jsonMarkupType

type JsonMarkupType = InferOutput<JsonMarkupTypeSchema>

// JsonMarkup is recursive, so we need to define the type first, then the schema.
type JsonMarkup = JsonMarkupType & {
  kind: 'block' | 'inline'
  children?: Array<JsonMarkup> | null
  textContent?: string | null
}

const jsonMarkup: GenericSchema<JsonMarkup> = intersect([
  jsonMarkupType,
  object({
    kind: picklist(['block', 'inline']),
    children: nullish(array(lazy(() => jsonMarkup))),
    textContent: nullish(string()),
  }),
])

export const richTextData = array(jsonMarkup)

export type RichTextData = InferOutput<typeof richTextData>

export const richTextContent = object({ json: array(jsonMarkup) })

export const richTextComponent = component('richText', richTextContent)

/**
 * Images
 *
 * @see https://crystallize.com/learn/developer-guides/catalogue-api/fetching-an-item/components#images
 */
export const image = object({
  url: pipe(string(), url()),
  altText: nullish(string()),
  caption: nullish(richTextContent),
  meta: nullish(array(object({
    key: nullish(string()),
    value: nullish(string()),
  }))),
  variants: nullish(
    array(object({
      url: pipe(string(), url()),
      width: number(),
    })),
  ),
})

export type Image = InferOutput<typeof image>

export type ImageVariant = NonNullable<Image['variants']>[number]

export const imagesData = object({
  firstImage: nullish(image),
  images: array(image),
})

export const singleImage = pipe(
  nullish(imagesData),
  transform(it => it?.firstImage ?? null),
)

export type ImagesData = InferOutput<typeof imagesData>

export const imagesComponent = component('images', imagesData)

/**
 * Videos
 *
 * @see https://crystallize.com/learn/developer-guides/catalogue-api/fetching-an-item/components#videos
 */
export const video = pipe(
  object({
    id: nullish(string()),
    title: nullish(string()),
    thumbnails: nullish(array(image)),
    src: nullish(string()),
  }),
  transform(it => ({
    ...it,
    src: it.src ?? (it.id ? `https://media.crystallize.com/${it.id}` : null),
  })),
)

export type Video = InferOutput<typeof video>

export const videosData = object({
  firstVideo: nullish(video),
  videos: array(video),
})

export type VideosData = InferOutput<typeof videosData>

export const videosComponent = component('videos', videosData)

/**
 * ParagraphCollection
 *
 * @see https://crystallize.com/learn/developer-guides/catalogue-api/fetching-an-item/components#paragraph-collection
 */
export const paragraph = object({
  title: nullish(singleLineContent),
  body: nullish(richTextContent),
  images: nullish(imagesData),
})

export type Paragraph = InferOutput<typeof paragraph>

export const paragraphCollectionData = array(paragraph)

export type ParagraphCollectionData = InferOutput<typeof paragraphCollectionData>

export const paragraphCollectionContent = object({
  paragraphs: paragraphCollectionData,
})

export const paragraphCollectionComponent = component('paragraphCollection', paragraphCollectionContent)

/**
 * ItemRelations
 *
 * @see https://crystallize.com/learn/developer-guides/catalogue-api/fetching-an-item/components#item-relation
 */
export const relatedDocument = object({
  id: string(),
  name: string(),
  path: string(),
})

export type RelatedDocumentData = InferOutput<typeof relatedDocument>

export const relatedDocumentsData = array(relatedDocument)

export const relatedProductVariantsData = array(
  object({
    sku: string(),
    name: string(),
  }),
)

export type ItemRelationsData = InferOutput<typeof relatedDocumentsData> | InferOutput<typeof relatedProductVariantsData>

export const itemRelationsContent = object({
  productVariants: nullish(relatedProductVariantsData),
  items: nullish(relatedDocumentsData),
})

export const itemRelationsComponent = component('itemRelations', itemRelationsContent)

export const baseComponents = union([
  booleanComponent,
  numericComponent,
  singleLineComponent,
  datetimeComponent,
  selectionComponent,
  imagesComponent,
  videosComponent,
  richTextComponent,
  paragraphCollectionComponent,
  itemRelationsComponent,
])

export type BaseComponent = InferOutput<typeof baseComponents>

/**
 * ContentChunk - Created outside the base components, since it references them (content chunk contains instances of other components)
 *
 * @see https://crystallize.com/learn/developer-guides/catalogue-api/fetching-an-item/components#content-chunk-component
 */
export const contentChunkData = array(
  array(
    union([
      baseComponents,
      // eslint-disable-next-line ts/no-use-before-define
      lazy(() => pieceComponent),
    ]),
  ),
)

export type ContentChunkData = InferOutput<typeof contentChunkData>

export const contentChunkContent = object({
  chunks: contentChunkData,
})

export const contentChunkComponent = component('contentChunk', contentChunkContent)

export type ContentChunkComponent = InferOutput<typeof contentChunkComponent>

/**
 * ComponentChoice
 *
 * @see https://crystallize.com/learn/developer-guides/catalogue-api/fetching-an-item/components#choice-component
 */
export const componentChoiceContent = object({
  selectedComponent: union([
    baseComponents,
    contentChunkComponent,
  ]),
})

export const componentChoiceComponent = component('componentChoice', componentChoiceContent)

export type ComponentChoiceComponent = InferOutput<typeof componentChoiceComponent>

/**
 * Piece - This is a special, recursive component, meaning it can contain instances of itself. Valibot allows it, but TS struggles. Solution is to create the type first, then the schema.
 *
 * @see https://crystallize.com/learn/concepts/pim/piece
 */
export const pieceData = array(
  union([
    baseComponents,
    contentChunkComponent,
    componentChoiceComponent,
    // eslint-disable-next-line ts/no-use-before-define
    lazy(() => pieceComponent),
    // eslint-disable-next-line ts/no-use-before-define
    lazy(() => multipleChoiceComponent),
  ]),
)

export type PieceData = InferOutput<typeof pieceData>

export interface PieceComponent {
  id: string
  type: 'piece'
  content?: null | {
    components: Array<
      BaseComponent | PieceComponent | ContentChunkComponent | MultipleChoiceComponent | ComponentChoiceComponent
    >
  }
}

export const pieceContent = object({
  components: pieceData,
})

type PieceComponentSchema = BaseSchema<unknown, PieceComponent, BaseIssue<unknown>>

export const pieceComponent: PieceComponentSchema = component('piece', pieceContent)

/**
 * MultipleChoice - Created outside the base components, since it references them (content chunk contains instances of other components)
 *
 * @see https://crystallize.com/learn/concepts/pim/structural-component/multiple-choice
 */
export const multipleChoiceData = array(
  union([
    baseComponents,
    contentChunkComponent,
    lazy(() => pieceComponent),
  ]),
)

export const multipleChoiceContent = object({
  selectedComponents: multipleChoiceData,
})

export const multipleChoiceComponent = component('componentMultipleChoice', multipleChoiceContent)

export type MultipleChoiceComponent = InferOutput<typeof multipleChoiceComponent>

// General stuff
export const componentSchema = union([
  baseComponents,
  contentChunkComponent,
  pieceComponent,
  multipleChoiceComponent,
  componentChoiceComponent,
])

export type Component = InferOutput<typeof componentSchema>

/**
 * Create component schema which accepts an array of raw Crystallize components and then transforms them into a more usable format.
 */
export function components<TOutput, TSchema extends GenericPipeItem<NormalizedComponents, TOutput>>(schema: TSchema) {
  return pipe(
    union([
      custom<NormalizedComponents>(it => !!it && !Array.isArray(it)),
      pipe(
        fallback(array(fallback(nullish(componentSchema), null)), []),
        transform(it => it.filter((c): c is NonNullable<typeof c> => c !== null)),
        transform(transformComponents),
      ),
    ]),
    schema,
  )
}
